import _ from 'underscore';
import React from 'react';
import { HelpIcon } from '@bingads-webui-campaign-react/help';
import { getBudgetChangeString } from '@bingads-webui-campaign/reports-v4-component';
import { aggragatorInsertionOrderReportData, getMismatchIconCount, isInsertionOrderMonthlyView, isInsertionOrderReport } from './insertion-order-helper';
import { isAggregatedOthersRow, isNormalRow, isTotalRow } from './row-helper';

/**
  * Uses canvas.measureText to compute and return the width of the given text of given font in pixels.
  *
  * @param {string} text The text to be rendered.
  * @param {string} font The css font descriptor that text is to be rendered with (e.g. "bold 14px verdana").
  * @returns {string} the width of the text in specific font.
  * @see https://stackoverflow.com/questions/118241/calculate-text-width-with-javascript/21015393#21015393
  */
function getTextWidth(text, font) {
  // re-use canvas object for better performance
  const canvas = getTextWidth.canvas || (getTextWidth.canvas = document.createElement('canvas'));
  const context = canvas.getContext('2d');
  context.font = font;
  const metrics = context.measureText(text);
  return metrics.width;
}

// This is well known.
// Cannot get the value by element, because this happens before rendering.
function getCanvasFont() {
  const fontWeight = '600';
  const fontSize = '14px';
  const fontFamily = 'Segoe UI';

  return `${fontWeight} ${fontSize} ${fontFamily}`;
}

/**
 * default value used in fluent-ui
 * reference https://github.com/microsoft/fluentui/blob/31345de30d356731eeb0ac1e658a2652f8f00aa7/packages/react/src/components/DetailsList/DetailsList.base.tsx
 */
const MIN_COLUMN_WIDTH = 120;
const MIN_FIRST_COLUMN_WIDTH = 140;

const conditionOperatorMap = {
  Between: (value, value1, value2) => value >= value1 && value <= value2,
  GreaterThan: (value, value1) => value > value1,
  GreaterThanEqual: (value, value1) => value >= value1,
  Equals: (value, value1) => Number(value) === Number(value1),
  DoesNotEqual: (value, value1) => Number(value) !== Number(value1),
  LessThan: (value, value1) => value < value1,
  LessThanEqual: (value, value1) => value <= value1,
};

function getItemValue(item, key, isDisplay = true) {
  if (_.isObject(item[key])) {
    return isDisplay ? item[key].Value : item[key].RawValue;
  }
  return item[key];
}

export function buildColumns(columnNames, reportSchemaColumns, conditionalFormats, i18n, customizeRenderParams) {
  // It seems `headerType` is useless, but we still keep it for future.
  function buildColumnBase(columnKey, columnOptions, index) {
    const columnOption = columnOptions[columnKey];
    const translatedName = i18n.getString(columnOption.name);
    const textWidth = getTextWidth(translatedName, getCanvasFont());
    const helpIconWidth = 23; // the icon is 22px width, add additional 1px for rounding.
    const headerWidth = textWidth + helpIconWidth;
    const defaultWidth = index ? MIN_COLUMN_WIDTH : MIN_FIRST_COLUMN_WIDTH;
    const finalMinWidth = Math.max(headerWidth, defaultWidth);

    return {
      key: columnKey,
      className: columnKey,
      minWidth: columnOption.width || finalMinWidth,
      maxWidth: 300,
      isResizable: true,
      index,
      name: translatedName,
      fieldName: columnKey,
      helpTopic: columnOption.helpTopic,
      type: columnOption.type,
      sortable: columnOption.sortable,
    };
  }

  function bindOnRenderHeader(column) {
    const onRenderHeader = columnProps => (
      <div >
        <span className="grid-header-text">
          {columnProps && columnProps.column.name}
        </span>
        {columnProps.column.helpTopic && <HelpIcon topic={columnProps.column.helpTopic} disabled />}
      </div >
    );

    return {
      ...column,
      onRenderHeader,
    };
  }

  function getConditionalFormatColor(value, conditions) {
    if (!conditions) {
      return 'initial';
    }
    let colorPicked = 'initial';
    conditions.forEach((condition) => {
      const conditionResult = conditionOperatorMap[condition.operator](value, condition.value, condition.value2);
      if (conditionResult) {
        colorPicked = colorPicked === 'initial' ? condition.color : colorPicked;
      }
    });
    return colorPicked;
  }

  function bindOnRenderRows(column) {
    const onRender = (item) => {
      const { reportParam, permissions, bindOnRenderRows: customizeBindOnRenderRows } = customizeRenderParams;
      let customizeRowNode = null;
      if (customizeRenderParams && !_.isEmpty(customizeRenderParams.bindOnRenderRows)) {
        // eslint-disable-next-line consistent-return
        customizeRowNode = _.chain(customizeBindOnRenderRows)
          .map(customizeRowFunction => customizeRowFunction({
            i18n,
            item,
            column,
            reportType: reportParam.reportType,
            permissions,
            viewType: reportParam.insertionOrderViewType,
            isAccountScope: customizeRenderParams.isAccountScope,
            filters: reportParam.filters,
          }))
          .compact(_.identity)
          .first()
          .value();
      }
      if (customizeRowNode) {
        return customizeRowNode;
      }

      const isOtherURLsCell = (item.key === 'Other URLs') && column.index === 0;
      if (isOtherURLsCell) {
        return (
          <div>
            <button className="urlButton" onClick={() => item.toggleIsReportModalOpen()}>
              {item.key}
            </button>
            <HelpIcon topic="pop_BA_Other_URLs" disabled />
          </div>
        );
      }

      const isTotalCell = (item.RowType === 'Total' || item.RowType === 'AggregatedOthers') && column.index === 0;
      if (isTotalCell) {
        return item.key;
      }

      if (column.key === 'SearchCategory') {
        if (item.isParent) {
          return (
            <div>
              <div className="searchCategoryText">
                {getItemValue(item, column.key)}
              </div>

              {!item.isCollapsed &&
                <div>
                  <button
                    className="searchCategoryDetailButton"
                    onClick={() => item.toggleIsSearchReportModalOpen()}
                  >
                    {i18n.getString(_TL_('View details'))}
                  </button>
                </div>
              }
            </div>
          );
        }
        if (item.SearchTermDetailsStr) {
          return (
            <div>
              <div style={{ fontWeight: 600 }}>
                {i18n.getString(_TL_('Search Terms'))}
              </div>
              <div style={{ whiteSpace: 'pre-wrap' }}>
                {item.SearchTermDetailsStr}
              </div>
            </div>
          );
        }
        if (_.some(columns, c => c.key === 'SearchQuery')) {
          return (<>{getItemValue(item, column.key)}</>);
        }
        return (
          <div>
            <div style={{ fontWeight: 600 }}>
              {i18n.getString(_TL_('Loading.....'))}
            </div>
          </div>
        );
      }

      if (column.key === 'LabelName') {
        const labelNameData = item[column.key];
        if (labelNameData) {
          const { Value, Color } = labelNameData;
          const colorCode = Number(Color).toString(16).padStart(6, '0');

          return (
            <div>
              <span style={{
                width: '15px',
                height: '15px',
                display: 'inline-block',
                marginRight: '5px',
                backgroundColor: `#${colorCode}`,
              }}
              />
              <span>{Value}</span>
            </div>
          );
        }

        return '';
      }

      return (<>{getItemValue(item, column.key)}</>);
    };
    return { ...column, onRender };
  }

  function bindColumnConditionalFormat(column, conditionalFormat) {
    const onRender = (item) => {
      const { reportParam, permissions } = customizeRenderParams;
      const color = item.RowType === 'Total' ? 'initial' : getConditionalFormatColor(getItemValue(item, column.key, false), conditionalFormat);
      let itemDisplayValue = getItemValue(item, column.key, true);
      if (isInsertionOrderReport({ reportType: reportParam.reportType, permissions })) {
        if (isNormalRow(item) && column.key === 'RemainingIOBudget') {
          itemDisplayValue = getBudgetChangeString({
            i18n,
            rawValue: getItemValue(item, column.key, false),
            amountValue: getItemValue(item, column.key, true),
            useEmptyString: false,
          });
        }
        if (column.key === 'RemainingIOBudget' && isTotalRow(item)) {
          itemDisplayValue = ''; // Hide value of total remaining IO budget
        }
      }
      return (<div className={`conditional-formatting-cell ${color !== 'initial' ? color.slice(1) : color}`} style={{ backgroundColor: color }}>{itemDisplayValue}</div >);
    };
    return { ...column, onRender };
  }

  if (!columnNames) {
    return [];
  }

  const columnOptions = reportSchemaColumns.categories
    .flatMap(category => category.options)
    .reduce((acc, current) => {
      acc[current.key] = current;
      return acc;
    }, {});

  const restrictedDisplayCols = [];
  if (reportSchemaColumns.displayRestricted) {
    reportSchemaColumns.displayRestricted.forEach(({ columns, isAllowed }) => {
      if (!isAllowed()) {
        columns.forEach(column => restrictedDisplayCols.push(column));
      }
    });
  }

  const columns = columnNames
    .filter(c => !restrictedDisplayCols.includes(c))
    .map((column, index) => buildColumnBase(column, columnOptions, index))
    .map(bindOnRenderHeader)
    .map(bindOnRenderRows);

  if (conditionalFormats) {
    const conditionalFormatsKey = Object.keys(conditionalFormats);
    if (conditionalFormatsKey.length > 0) {
      const conditionFormattedColumn = columns.map((column) => {
        if (conditionalFormatsKey.includes(column.key)) {
          return bindColumnConditionalFormat(column, conditionalFormats[column.key]);
        }
        return column;
      });
      return conditionFormattedColumn;
    }
  }

  return columns;
}

function buildRows(rowDefinitions, i18n, totalCount, reportParam, permissions, ctrLogger) {
  const baseRowDefinitions = aggragatorInsertionOrderReportData({ rowDefinitions, reportParam, permissions });
  const mismatchIconCount = getMismatchIconCount({ items: baseRowDefinitions, reportParam, permissions });
  if (mismatchIconCount > -1) {
    ctrLogger.click({ target: 'mismatchIconCount', extraData: { reportType: 'InsertionOrderReport', mismatchIconCount } });
  }
  return baseRowDefinitions.map((rowDefinition, index) => {
    if (isTotalRow(rowDefinition) || isAggregatedOthersRow(rowDefinition)) {
      const rowNumber = isInsertionOrderMonthlyView({ reportType: reportParam.reportType, permissions, viewType: reportParam.insertionOrderViewType }) ? baseRowDefinitions.length - 1 : totalCount;
      return { ...rowDefinition, key: i18n.getString(_TL_('Overall total - {{number}} row(s)'), { number: rowNumber }) };
    }
    return { ...rowDefinition, key: index };
  });
}

export function extractGridItemsFromReportDataResponseBody(reportDataResponseBody, i18n, reportParam, permissions, ctrLogger) {
  if (reportDataResponseBody && reportDataResponseBody.value && Array.isArray(reportDataResponseBody.value)) {
    if (reportDataResponseBody.value.length > 0) {
      // Old API
      if (reportDataResponseBody.value[0].RowId === -1) {
        const [, ...columnRowDefinitions] = reportDataResponseBody.value;
        return buildRows(columnRowDefinitions, i18n, reportDataResponseBody['@odata.count'], reportParam, permissions, ctrLogger);
      }

      // New API
      return buildRows(reportDataResponseBody.value, i18n, reportDataResponseBody['@odata.count'], reportParam, permissions, ctrLogger);
    }
    return [];
  }
  return undefined;
}

export function hasDifferentTimeColumns(oldColumns, newColumns, reportSchema) {
  const { categories } = reportSchema.Columns;
  const timeCategory = _.find(categories, item => item.type === 'TimeColumns');
  if (!timeCategory) {
    return false;
  }
  const allTimeColumns = (timeCategory.options || []).map(item => item.key);
  const oldTimeColumn = _.intersection(allTimeColumns, oldColumns);
  const newTimeColumn = _.intersection(allTimeColumns, newColumns);
  return !_.isEqual(oldTimeColumn, newTimeColumn);
}
